import {
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@heroui/button';
import { Card, CardBody, CardHeader } from '@heroui/card';
import { Chip } from '@heroui/chip';
import { Divider } from '@heroui/divider';
import { Input, Textarea } from '@heroui/input';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from '@heroui/modal';
import { Pagination } from '@heroui/pagination';
import { Select, SelectItem } from '@heroui/select';
import { Spinner } from '@heroui/spinner';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import SEOHead from '../../components/SEOHead';
import { ticketService } from '../../services/ticketService';
import {
  CreateTicketRequest,
  CustomerServiceTicket,
  TicketCategory,
  TicketPriority,
} from '../../types/tickets';

const statusOptions = [
  { key: '', label: 'All Statuses' },
  { key: 'Open', label: 'Open' },
  { key: 'InProgress', label: 'In Progress' },
  { key: 'AwaitingCustomer', label: 'Awaiting Customer' },
  { key: 'Resolved', label: 'Resolved' },
  { key: 'Closed', label: 'Closed' },
];

const categoryOptions = [
  { key: '', label: 'All Categories' },
  { key: 'General', label: 'General' },
  { key: 'OrderIssue', label: 'Order Issue' },
  { key: 'ShippingIssue', label: 'Shipping Issue' },
  { key: 'PaymentIssue', label: 'Payment Issue' },
  { key: 'ProductQuality', label: 'Product Quality' },
  { key: 'TechnicalSupport', label: 'Technical Support' },
  { key: 'ReturnRefund', label: 'Return/Refund' },
  { key: 'ProductInquiry', label: 'Product Inquiry' },
];

const categoryOptionsForCreate = categoryOptions.filter(
  option => option.key !== ''
);

const priorityOptions = [
  { key: 'Low', label: 'Low' },
  { key: 'Medium', label: 'Medium' },
  { key: 'High', label: 'High' },
  { key: 'Urgent', label: 'Urgent' },
];

const Tickets: React.FC = () => {
  const pageTitle = 'Support Tickets';
  const pageDescription = 'View and manage your support requests.';
  const [tickets, setTickets] = useState<CustomerServiceTicket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedTicket, setSelectedTicket] =
    useState<CustomerServiceTicket | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  const {
    isOpen: isCreateOpen,
    onOpen: onCreateOpen,
    onClose: onCreateClose,
  } = useDisclosure();
  const {
    isOpen: isViewOpen,
    onOpen: onViewOpen,
    onClose: onViewClose,
  } = useDisclosure();

  const [createForm, setCreateForm] = useState<CreateTicketRequest>({
    subject: '',
    category: 'General' as TicketCategory,
    priority: 'Medium' as TicketPriority,
    description: '',
  });

  const fetchTickets = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await ticketService.getTickets({
        status: statusFilter || undefined,
        category: categoryFilter || undefined,
        page: currentPage,
        pageSize: 10,
      });
      setTickets(response.tickets || []);
      setTotalPages(response.totalPages || 1);
    } catch (error: any) {
      toast.error('Failed to fetch tickets');
    } finally {
      setIsLoading(false);
    }
  }, [categoryFilter, currentPage, statusFilter]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleCreateTicket = async () => {
    try {
      await ticketService.createTicket(createForm);
      toast.success('Support ticket created successfully');
      onCreateClose();
      setCreateForm({
        subject: '',
        category: 'General' as TicketCategory,
        priority: 'Medium' as TicketPriority,
        description: '',
      });
      fetchTickets();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create ticket');
    }
  };

  const handleViewTicket = async (ticketId: number) => {
    try {
      const ticket = await ticketService.getTicket(ticketId);
      setSelectedTicket(ticket);
      onViewOpen();
    } catch (error: any) {
      toast.error('Failed to load ticket details');
    }
  };

  const handleSendMessage = async () => {
    if (!selectedTicket || !newMessage.trim()) return;

    setIsSendingMessage(true);
    try {
      const updatedTicket = await ticketService.addTicketMessage(
        selectedTicket.id,
        {
          message: newMessage.trim(),
        }
      );
      setSelectedTicket(updatedTicket);
      setNewMessage('');
      toast.success('Message sent successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send message');
    } finally {
      setIsSendingMessage(false);
    }
  };

  const getStatusColor = (
    status: string
  ): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' => {
    switch (status) {
      case 'Open':
        return 'primary';
      case 'InProgress':
        return 'warning';
      case 'AwaitingCustomer':
        return 'secondary';
      case 'Resolved':
        return 'success';
      case 'Closed':
        return 'default';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (
    priority: string
  ): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' => {
    switch (priority) {
      case 'Low':
        return 'default';
      case 'Medium':
        return 'primary';
      case 'High':
        return 'warning';
      case 'Urgent':
        return 'danger';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Open':
        return <ExclamationCircleIcon className='w-3 h-3' />;
      case 'InProgress':
        return <ClockIcon className='w-3 h-3' />;
      case 'AwaitingCustomer':
        return <ExclamationTriangleIcon className='w-3 h-3' />;
      case 'Resolved':
        return <CheckCircleIcon className='w-3 h-3' />;
      case 'Closed':
        return <CheckCircleIcon className='w-3 h-3' />;
      default:
        return null;
    }
  };

  const formatCategoryLabel = (category: string) => {
    switch (category) {
      case 'OrderIssue':
        return 'Order Issue';
      case 'ShippingIssue':
        return 'Shipping Issue';
      case 'PaymentIssue':
        return 'Payment Issue';
      case 'ProductQuality':
        return 'Product Quality';
      case 'TechnicalSupport':
        return 'Technical Support';
      case 'ReturnRefund':
        return 'Return/Refund';
      case 'ProductInquiry':
        return 'Product Inquiry';
      default:
        return category;
    }
  };

  const formatStatusLabel = (status: string) => {
    switch (status) {
      case 'InProgress':
        return 'In Progress';
      case 'AwaitingCustomer':
        return 'Awaiting Customer';
      default:
        return status;
    }
  };

  return (
    <>
      <SEOHead title={pageTitle} description={pageDescription} />
      <div className='space-y-6'>
        {/* Header */}
        <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4'>
          <div>
            <h1 className='text-2xl font-bold text-gray-900'>
              Support Tickets
            </h1>
            <p className='text-gray-600'>
              Manage your support requests and get help
            </p>
          </div>
          <Button
            color='primary'
            startContent={<PlusIcon className='w-4 h-4' />}
            onClick={onCreateOpen}
          >
            Create Ticket
          </Button>
        </div>

        {/* Tickets List */}
        <Card>
          <CardHeader>
            <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 w-full'>
              <h2 className='text-xl font-semibold'>My Tickets</h2>
              <div className='flex flex-col sm:flex-row gap-2'>
                <Select
                  placeholder='Filter by status'
                  className='w-full sm:w-48'
                  selectedKeys={statusFilter ? [statusFilter] : []}
                  items={statusOptions}
                  onSelectionChange={keys => {
                    const selected = Array.from(keys)[0] as string;
                    setStatusFilter(selected || '');
                  }}
                >
                  {option => (
                    <SelectItem key={option.key}>{option.label}</SelectItem>
                  )}
                </Select>

                <Select
                  placeholder='Filter by category'
                  className='w-full sm:w-48'
                  selectedKeys={categoryFilter ? [categoryFilter] : []}
                  items={categoryOptions}
                  onSelectionChange={keys => {
                    const selected = Array.from(keys)[0] as string;
                    setCategoryFilter(selected || '');
                  }}
                >
                  {option => (
                    <SelectItem key={option.key}>{option.label}</SelectItem>
                  )}
                </Select>
              </div>
            </div>
          </CardHeader>

          <CardBody>
            {isLoading ? (
              <div className='flex justify-center py-8'>
                <Spinner size='lg' />
              </div>
            ) : tickets.length === 0 ? (
              <div className='text-center py-8'>
                <ChatBubbleLeftRightIcon className='w-12 h-12 text-gray-400 mx-auto mb-4' />
                <p className='text-gray-500 mb-2'>No support tickets found</p>
                <p className='text-gray-400 text-sm'>
                  Create your first ticket to get help
                </p>
              </div>
            ) : (
              <div className='space-y-4'>
                {tickets.map(ticket => (
                  <Card key={ticket.id} className='border'>
                    <CardBody>
                      <div className='flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4'>
                        <div className='flex-1'>
                          <div className='flex items-center space-x-3 mb-2'>
                            <h3 className='font-semibold'>
                              #{ticket.ticketNumber}
                            </h3>
                            <Chip
                              color={getStatusColor(ticket.status)}
                              variant='flat'
                              startContent={getStatusIcon(ticket.status)}
                              size='sm'
                            >
                              {formatStatusLabel(ticket.status)}
                            </Chip>
                            <Chip
                              color={getPriorityColor(ticket.priority)}
                              variant='flat'
                              size='sm'
                            >
                              {ticket.priority}
                            </Chip>
                          </div>

                          <h4 className='font-medium text-lg mb-2'>
                            {ticket.subject}
                          </h4>

                          <div className='grid grid-cols-1 md:grid-cols-3 gap-4 text-sm'>
                            <div>
                              <p className='text-gray-500'>Category</p>
                              <p className='font-medium'>
                                {formatCategoryLabel(ticket.category)}
                              </p>
                            </div>
                            <div>
                              <p className='text-gray-500'>Created</p>
                              <p className='font-medium'>
                                {new Date(
                                  ticket.createdAt
                                ).toLocaleDateString()}
                              </p>
                            </div>
                            <div>
                              <p className='text-gray-500'>Messages</p>
                              <p className='font-medium'>
                                {ticket.messages.length}
                              </p>
                            </div>
                          </div>

                          {ticket.orderNumber && (
                            <div className='mt-3 p-2 bg-blue-50 rounded-lg'>
                              <p className='text-sm font-medium text-blue-900'>
                                Related to Order #{ticket.orderNumber}
                              </p>
                            </div>
                          )}
                        </div>

                        <div className='flex flex-col sm:flex-row gap-2'>
                          <Button
                            size='sm'
                            variant='bordered'
                            onClick={() => handleViewTicket(ticket.id)}
                            startContent={<EyeIcon className='w-4 h-4' />}
                          >
                            View Details
                          </Button>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <div className='flex justify-center mt-6'>
                <Pagination
                  total={totalPages}
                  page={currentPage}
                  onChange={setCurrentPage}
                />
              </div>
            )}
          </CardBody>
        </Card>

        {/* Create Ticket Modal */}
        <Modal isOpen={isCreateOpen} onClose={onCreateClose} size='2xl'>
          <ModalContent>
            <ModalHeader>
              <h3>Create Support Ticket</h3>
            </ModalHeader>
            <ModalBody>
              <div className='space-y-4'>
                <Input
                  label='Subject'
                  placeholder='Brief description of your issue'
                  value={createForm.subject}
                  onChange={e =>
                    setCreateForm(prev => ({
                      ...prev,
                      subject: e.target.value,
                    }))
                  }
                  isRequired
                />

                <div className='grid grid-cols-2 gap-4'>
                  <Select
                    label='Category'
                    selectedKeys={[createForm.category]}
                    items={categoryOptionsForCreate}
                    onSelectionChange={keys => {
                      const selected = Array.from(keys)[0] as TicketCategory;
                      setCreateForm(prev => ({ ...prev, category: selected }));
                    }}
                  >
                    {option => (
                      <SelectItem key={option.key}>{option.label}</SelectItem>
                    )}
                  </Select>

                  <Select
                    label='Priority'
                    selectedKeys={[createForm.priority || 'Medium']}
                    items={priorityOptions}
                    onSelectionChange={keys => {
                      const selected = Array.from(keys)[0] as TicketPriority;
                      setCreateForm(prev => ({ ...prev, priority: selected }));
                    }}
                  >
                    {option => (
                      <SelectItem key={option.key}>{option.label}</SelectItem>
                    )}
                  </Select>
                </div>

                <Textarea
                  label='Description'
                  placeholder='Please provide detailed information about your issue...'
                  value={createForm.description}
                  onChange={e =>
                    setCreateForm(prev => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  minRows={4}
                  isRequired
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant='bordered' onClick={onCreateClose}>
                Cancel
              </Button>
              <Button
                color='primary'
                onClick={handleCreateTicket}
                isDisabled={
                  !createForm.subject.trim() || !createForm.description.trim()
                }
              >
                Create Ticket
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* View Ticket Modal */}
        {selectedTicket && (
          <Modal
            isOpen={isViewOpen}
            onClose={onViewClose}
            size='4xl'
            scrollBehavior='inside'
          >
            <ModalContent>
              <ModalHeader>
                <div className='flex items-center space-x-3'>
                  <h3>Ticket #{selectedTicket.ticketNumber}</h3>
                  <Chip
                    color={getStatusColor(selectedTicket.status)}
                    variant='flat'
                    startContent={getStatusIcon(selectedTicket.status)}
                    size='sm'
                  >
                    {formatStatusLabel(selectedTicket.status)}
                  </Chip>
                  <Chip
                    color={getPriorityColor(selectedTicket.priority)}
                    variant='flat'
                    size='sm'
                  >
                    {selectedTicket.priority}
                  </Chip>
                </div>
              </ModalHeader>
              <ModalBody>
                <div className='space-y-6'>
                  {/* Ticket Details */}
                  <div>
                    <h4 className='font-semibold text-lg mb-2'>
                      {selectedTicket.subject}
                    </h4>
                    <div className='grid grid-cols-2 gap-4 text-sm'>
                      <div>
                        <p className='text-gray-500'>Category</p>
                        <p className='font-medium'>
                          {formatCategoryLabel(selectedTicket.category)}
                        </p>
                      </div>
                      <div>
                        <p className='text-gray-500'>Created</p>
                        <p className='font-medium'>
                          {new Date(
                            selectedTicket.createdAt
                          ).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  <Divider />

                  {/* Messages */}
                  <div className='space-y-4'>
                    <h5 className='font-medium'>Conversation</h5>
                    <div className='space-y-4 max-h-96 overflow-y-auto'>
                      {selectedTicket.messages.map((message, index) => (
                        <div
                          key={index}
                          className={`p-4 rounded-lg ${
                            message.isFromCustomer
                              ? 'bg-blue-50 ml-4'
                              : 'bg-gray-50 mr-4'
                          }`}
                        >
                          <div className='flex justify-between items-start mb-2'>
                            <p className='font-medium text-sm'>
                              {message.isFromCustomer ? 'You' : 'Support Team'}
                            </p>
                            <p className='text-xs text-gray-500'>
                              {new Date(message.createdAt).toLocaleString()}
                            </p>
                          </div>
                          <p className='text-gray-700'>{message.message}</p>
                        </div>
                      ))}
                    </div>

                    {/* Send Message */}
                    {selectedTicket.status !== 'Closed' && (
                      <div className='space-y-3'>
                        <Textarea
                          placeholder='Type your message...'
                          value={newMessage}
                          onChange={e => setNewMessage(e.target.value)}
                          minRows={3}
                        />
                        <div className='flex justify-end'>
                          <Button
                            color='primary'
                            onClick={handleSendMessage}
                            isLoading={isSendingMessage}
                            isDisabled={!newMessage.trim()}
                          >
                            Send Message
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button variant='bordered' onClick={onViewClose}>
                  Close
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        )}
      </div>
    </>
  );
};

export default Tickets;
